/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef } from "react";
import { TableProps } from "../../../interfaces/table";
import useModals from "../../../hooks/useModals";
import { useTimeStore } from "../../../stores/time";
import {
  Badge,
  Checkbox,
  DesktopTable,
  MicroLabel,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  cardClasses,
  emptyStateIcon,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TableHolidays = ({ columns, data }: TableProps) => {
  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Modals
  const { modals, openModals } = useModals();

  const { idTimesSelected, setIdTimesSelected } = useTimeStore();

  const handleRowClickAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.target.checked) {
      setIdTimesSelected(data.map((time) => time.id));
    } else {
      setIdTimesSelected([]);
    }
  };

  // Fonction pour gérer la checkbox sur une ligne
  const handleRowClickCheck = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    if (e.target.checked) {
      setIdTimesSelected((prev) => [...prev, id]);
    } else {
      setIdTimesSelected((prev) => prev.filter((time) => time !== id));
    }
  };

  const [primaryColumn, ...secondaryColumns] = columns;

  const renderCell = (col: any, row: any) => {
    if (col.accessor === "customerFile" && row[col.accessor] === null) {
      return <Badge tone="amber">Administratif</Badge>;
    }
    if (col.accessor === "mission" && row[col.accessor] === null) {
      return <span className="text-slate-500 italic">{row.timesLabel}</span>;
    }
    return renderCellValue(row[col.accessor]);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState
            title="Aucune demande de congés / absence trouvée"
            description="Les demandes de congés et d'absences apparaîtront ici."
            icon={emptyStateIcon(
              "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            )}
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  <Th className="w-px">
                    <Checkbox
                      label="Tout sélectionner"
                      checked={
                        idTimesSelected.length === data.length && data.length > 0
                      }
                      onChange={(e) => handleRowClickAll(e)}
                    />
                  </Th>
                  {columns.map((col, index) => (
                    <Th key={index}>{col.header}</Th>
                  ))}
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const isSelected = idTimesSelected.includes(row.id);

                  return (
                    <tr
                      key={rowIndex}
                      className={rowClasses({
                        selected: isSelected,
                        clickable: false,
                      })}
                    >
                      <Td>
                        <Checkbox
                          label="Sélectionner la ligne"
                          checked={isSelected}
                          onChange={(e) => handleRowClickCheck(e, row.id)}
                        />
                      </Td>
                      {columns.map((col, colIndex) => (
                        <Td
                          key={colIndex}
                          className={
                            colIndex === 0
                              ? "font-semibold text-slate-900"
                              : "text-slate-600"
                          }
                        >
                          {renderCell(col, row)}
                        </Td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const isSelected = idTimesSelected.includes(row.id);

                return (
                  <li key={rowIndex}>
                    <div className={cardClasses({ selected: isSelected })}>
                      <div className="flex items-start gap-3">
                        <span className="pt-1">
                          <Checkbox
                            label="Sélectionner la ligne"
                            checked={isSelected}
                            onChange={(e) => handleRowClickCheck(e, row.id)}
                          />
                        </span>
                        <div className="min-w-0">
                          <MicroLabel>{primaryColumn?.header}</MicroLabel>
                          <div className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                            {primaryColumn
                              ? renderCell(primaryColumn, row)
                              : null}
                          </div>
                        </div>
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCell(col, row)}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}
                    </div>
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableHolidays;
